// Path: sec-parser/client/src/components/common/ProtectedRoute.jsx
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Spin } from "antd";
import { getMe } from "../../services/api";
import { setUser, clearAuth } from "../../store";

/**
 * Wraps any route that requires authentication.
 * On first load, validates the stored token by calling /api/auth/me.
 * If token is invalid/expired, redirects to /login.
 *
 * Usage:
 *   <Route path="upload" element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
 *
 * Role guard (optional):
 *   <Route path="users" element={<ProtectedRoute role="admin"><UsersPage /></ProtectedRoute>} />
 */
export default function ProtectedRoute({ children, role }) {
  const dispatch    = useDispatch();
  const location    = useLocation();
  const { isLoggedIn, user } = useSelector((s) => s.auth);
  const [checking, setChecking] = useState(!user); // Only check if we don't have user yet

  useEffect(() => {
    if (!user && isLoggedIn) {
      getMe()
        .then(({ data }) => { dispatch(setUser(data)); setChecking(false); })
        .catch(() => { dispatch(clearAuth()); setChecking(false); });
    } else {
      setChecking(false);
    }
  }, []);

  if (checking) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Spin size="large" tip="Loading…" />
      </div>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Role guard
  if (role && user?.role !== role) {
    return <Navigate to="/upload" replace />;
  }

  return children;
}